/**
 * Pure decision logic for the "Book Now" quick-checkout A/B test (KFT-3914).
 * Kept free of Nuxt/composable imports so it can be unit-tested in isolation;
 * the reactive wiring lives in `useBookNowExperiment`.
 */

export type BookNowVariant = 'control' | 'variation'
export type BookNowItemCategory = 'ADD-TO-CART' | 'BUY-NOW'
export type GiftcardType = 'VALUE' | 'EVENT' | 'SPECIAL'

export interface QuickCheckoutContext {
  isBoxEvent?: boolean
  isKitsPage?: boolean
  isEmbedded?: boolean
  giftcardType?: GiftcardType | null
  /** Whether the event offers any (eligible) add-ons on this page. */
  eventHasAddOns?: boolean
  /** Whether the buyer has picked at least one add-on. */
  hasSelectedAddOn?: boolean
  /**
   * Whether the cart already holds items *before* this add. Quick-checkout only
   * triggers on the first item of the session; a second item opens the sidebar
   * so the buyer can batch multiple products.
   */
  cartHasItems?: boolean
}

/**
 * Event giftcards may move to Book Now once the personalization flow ships.
 * Value giftcards never quick-checkout. Flip this one constant when product is
 * ready to send Event giftcards straight to checkout.
 */
export const BOOK_NOW_EVENT_GIFTCARDS = false

/**
 * The bucket is persisted in this cookie so it survives hard navigation (product
 * cards open event pages in a new tab). It is read on the client only — never at
 * SSR time, see `useBookNowExperiment` — and encodes:
 * `'true'` → variation, `'false'` → control, absent → default (not bucketed).
 *
 * **Bump the trailing number whenever the VWO campaign is restarted.** A new
 * campaign re-randomises visitors, so a cookie written under the previous run
 * would pin a returning visitor to the wrong arm until VWO's call lands. A new
 * name makes every cookie in the wild inert at once, and doubles as the kill
 * switch if the campaign is stopped and nothing is left to correct the bucket.
 */
export const BOOK_NOW_COOKIE = 'exp-book-now-2'

export const parseBookNowCookie = (
  value: string | null | undefined
): BookNowVariant | null => {
  if (value === 'true') return 'variation'
  if (value === 'false') return 'control'
  return null
}

export const variantToBookNowCookie = (variant: BookNowVariant): string =>
  variant === 'variation' ? 'true' : 'false'

/**
 * GA4 `item_category4` used to segment the experiment. Returns undefined for the
 * default (not-bucketed) state so those users stay out of both GA buckets.
 */
export const resolveItemCategory4 = (
  variant: BookNowVariant | null | undefined
): BookNowItemCategory | undefined => {
  if (variant === 'variation') return 'BUY-NOW'
  if (variant === 'control') return 'ADD-TO-CART'
  return undefined
}

/**
 * Whether this purchase skips the cart and goes straight to checkout. Kits, box
 * events and embedded flows always keep add-to-cart; giftcards are gated behind
 * {@link BOOK_NOW_EVENT_GIFTCARDS} (Value giftcards never quick-checkout).
 */
export const resolveQuickCheckout = (
  isVariation: boolean,
  ctx: QuickCheckoutContext = {},
  eventGiftcardsEnabled: boolean = BOOK_NOW_EVENT_GIFTCARDS
): boolean => {
  if (!isVariation) return false
  if (ctx.isEmbedded || ctx.isBoxEvent || ctx.isKitsPage) return false
  // Anything already in the cart means the buyer is batching — keep them in the
  // sidebar so they can review/add more; only the first item quick-checks out.
  if (ctx.cartHasItems) return false
  if (ctx.giftcardType) {
    return eventGiftcardsEnabled && ctx.giftcardType !== 'VALUE'
  }
  // When the event offers add-ons, only go straight to checkout once the buyer
  // has picked one; otherwise open the cart sidebar so they can add one. Events
  // without add-ons go straight to checkout.
  if (ctx.eventHasAddOns && !ctx.hasSelectedAddOn) return false
  return true
}
